<template>
  <div class="app-shell">
    <aside class="sidebar">
      <GlobalHeader />

      <nav class="side-nav">
        <button
          v-for="item in menuItems"
          :key="item.path"
          class="nav-item"
          :class="{ active: activeMenu === item.path }"
          @click="handleMenuClick(item.path)"
        >
          <span>{{ item.title }}</span>
        </button>

        <template v-if="isAdmin">
          <div class="nav-divider">Admin</div>
          <button
            v-for="item in adminMenuItems"
            :key="item.path"
            class="nav-item admin"
            :class="{ active: activeMenu === item.path }"
            @click="handleMenuClick(item.path)"
          >
            <span>{{ item.title }}</span>
            <a-badge
              v-if="getAdminMenuCount(item) > 0"
              :count="getAdminMenuCount(item)"
              :max-count="99"
              class="nav-count"
            />
          </button>
        </template>
      </nav>

      <div class="user-card" :class="{ authenticated: loginUser?.id }">
        <div class="user-card-bg" />

        <a-popover
          v-if="loginUser?.id"
          trigger="click"
          position="right"
          @popup-visible-change="handleNotificationPopup"
        >
          <div class="notification-anchor">
            <a-badge :count="unreadCount" :max-count="99">
              <button
                class="notification-trigger"
                type="button"
                title="消息通知"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M18 10.5c0-3.3-2.1-5.7-5-6.2V3a1 1 0 0 0-2 0v1.3c-2.9.5-5 2.9-5 6.2v3.2l-1.3 2.1A1.1 1.1 0 0 0 5.6 17h12.8a1.1 1.1 0 0 0 .9-1.7L18 13.7v-3.2ZM9.6 18.2a2.5 2.5 0 0 0 4.8 0"
                  />
                </svg>
              </button>
            </a-badge>
          </div>

          <template #content>
            <div class="notification-popover">
              <div class="notification-popover-head">
                <strong>消息通知</strong>
                <span>{{ unreadCount }} 条未读</span>
              </div>

              <div class="notification-summary">
                <div>
                  <span>回复</span>
                  <strong>{{ unreadSummary.replyUnread || 0 }}</strong>
                </div>
                <div>
                  <span>点赞</span>
                  <strong>{{ unreadSummary.thumbUnread || 0 }}</strong>
                </div>
                <div>
                  <span>收藏</span>
                  <strong>{{ unreadSummary.favourUnread || 0 }}</strong>
                </div>
                <div>
                  <span>系统</span>
                  <strong>{{ unreadSummary.systemUnread || 0 }}</strong>
                </div>
              </div>

              <div class="notification-popover-actions">
                <a-button size="mini" type="primary" @click="goNotifications">
                  查看消息
                </a-button>
                <a-button
                  size="mini"
                  :disabled="unreadCount === 0"
                  @click="handleReadAllNotifications"
                >
                  全部已读
                </a-button>
              </div>
            </div>
          </template>
        </a-popover>

        <div class="user-summary">
          <button class="avatar-button" type="button" @click="goUserProfile">
            <a-avatar :size="42" class="avatar">
              <img
                v-if="loginUser?.userAvatar"
                :src="loginUser.userAvatar"
                alt=""
              />
              <span v-else>{{ getDefaultAvatarText(displayName) }}</span>
            </a-avatar>
          </button>

          <a-dropdown trigger="click" @select="handleUserMenuSelect">
            <button class="dropdown-trigger" type="button" title="用户菜单">
              <span v-if="hasUserReviewTodo" class="review-dot" />
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m7 10 5 5 5-5" />
              </svg>
            </button>
            <template #content>
              <a-doption value="profile">个人中心</a-doption>
              <a-doption value="space">
                <span class="space-option">
                  我的空间
                  <span v-if="hasUserReviewTodo" class="space-option-dot" />
                </span>
              </a-doption>
              <a-divider v-if="loginUser?.id" :margin="4" />
              <a-doption v-if="loginUser?.id" value="logout">
                退出登录
              </a-doption>
            </template>
          </a-dropdown>
        </div>

        <div class="user-copy">
          <div class="welcome">
            {{ loginUser?.id ? "Welcome back" : "Welcome" }}
          </div>
          <div class="name">{{ displayName }}</div>
        </div>

        <div class="user-actions">
          <a-button
            v-if="loginUser?.id"
            class="logout-button"
            size="small"
            status="danger"
            type="text"
            :loading="logoutLoading"
            @click="handleLogout"
          >
            退出登录
          </a-button>
          <a-button
            v-else
            size="mini"
            type="primary"
            @click="router.push('/user/login')"
          >
            登录
          </a-button>
        </div>
      </div>
    </aside>

    <main class="main-panel">
      <router-view />
    </main>

    <footer class="site-footer">
      <a
        href="https://beian.miit.gov.cn/"
        target="_blank"
        rel="noopener noreferrer"
      >
        陕ICP备2026023999号
      </a>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { Message } from "@arco-design/web-vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import {
  getNotificationUnreadCount,
  readAllNotification,
} from "@/api/notification";
import { AdminReviewTodoVO, getReviewTodoCount } from "@/api/admin";
import { getMyReviewTodoCount } from "@/api/user";
import GlobalHeader from "@/components/GlobalHeader.vue";
import { RootState } from "@/store";
import { NotificationUnreadVO } from "@/types/notification";
import { UserReviewTodoVO } from "@/types/user";
import { getDefaultAvatarText } from "@/utils/avatar";

const route = useRoute();
const router = useRouter();
const store = useStore<RootState>();
const logoutLoading = ref(false);
const unreadCount = ref(0);
const unreadSummary = ref<NotificationUnreadVO>({});
const adminReviewTodo = ref<AdminReviewTodoVO>({});
const userReviewTodo = ref<UserReviewTodoVO>({});

const menuItems = [
  { path: "/problems", title: "题库" },
  { path: "/posts", title: "讨论" },
  { path: "/submissions/my", title: "提交记录" },
  { path: "/score/stats", title: "排行榜" },
  { path: "/score/records", title: "积分记录" },
];

const adminMenuItems = [
  { path: "/admin", title: "后台首页" },
  { path: "/admin/users", title: "用户管理" },
  { path: "/admin/problems", title: "题目管理" },
  { path: "/admin/submissions", title: "提交查看" },
  { path: "/admin/posts", title: "帖子审核", countKey: "pendingPostCount" },
  { path: "/admin/post-categories", title: "帖子板块" },
  { path: "/admin/reports", title: "举报管理" },
  {
    path: "/admin/comments",
    title: "评论审核",
    countKey: "pendingProblemCommentCount",
  },
  {
    path: "/admin/post-comments",
    title: "帖子评论审核",
    countKey: "pendingPostCommentCount",
  },
];

const activeMenu = computed(() => String(route.meta.menuKey || route.path));
const loginUser = computed(() => store.state.user.loginUser);
const isAdmin = computed(() => loginUser.value?.userRole === "admin");
const userReviewTodoCount = computed(() =>
  Number(userReviewTodo.value.totalPendingCount || 0)
);
const hasUserReviewTodo = computed(() => userReviewTodoCount.value > 0);
const displayName = computed(
  () =>
    loginUser.value?.userName ||
    loginUser.value?.userAccount ||
    `User ${loginUser.value?.id || ""}`
);

const handleMenuClick = (key: string) => {
  router.push(key);
};

const getAdminMenuCount = (item: { countKey?: keyof AdminReviewTodoVO }) => {
  return item.countKey ? Number(adminReviewTodo.value[item.countKey] || 0) : 0;
};

const goUserProfile = () => {
  if (loginUser.value?.id) {
    router.push("/user/profile");
    return;
  }
  router.push("/user/login");
};

const goNotifications = () => {
  router.push("/notifications");
};

const handleUserMenuSelect = (
  value: string | number | Record<string, unknown>
) => {
  if (value === "profile") {
    router.push("/user/profile");
    return;
  }
  if (value === "space") {
    router.push("/user/space");
    return;
  }
  if (value === "logout") {
    void handleLogout();
  }
};

const handleLogout = async () => {
  logoutLoading.value = true;
  try {
    await store.dispatch("user/logout");
    Message.success("已退出登录");
    router.push("/user/login");
  } finally {
    logoutLoading.value = false;
  }
};

const fetchUnreadCount = async () => {
  if (!loginUser.value?.id) {
    unreadCount.value = 0;
    unreadSummary.value = {};
    return;
  }
  try {
    const data = await getNotificationUnreadCount();
    unreadSummary.value = data || {};
    unreadCount.value = Number(data.totalUnread || 0);
  } catch (error) {
    unreadCount.value = 0;
    unreadSummary.value = {};
  }
};

const fetchAdminReviewTodo = async () => {
  if (!isAdmin.value) {
    adminReviewTodo.value = {};
    return;
  }
  try {
    adminReviewTodo.value = (await getReviewTodoCount()) || {};
  } catch (error) {
    adminReviewTodo.value = {};
  }
};

const fetchUserReviewTodo = async () => {
  if (!loginUser.value?.id) {
    userReviewTodo.value = {};
    return;
  }
  try {
    userReviewTodo.value = (await getMyReviewTodoCount()) || {};
  } catch (error) {
    userReviewTodo.value = {};
  }
};

const handleNotificationPopup = (visible: boolean) => {
  if (visible) {
    fetchUnreadCount();
  }
};

const handleReadAllNotifications = async () => {
  await readAllNotification();
  Message.success("已全部标记为已读");
  await fetchUnreadCount();
};

watch(
  () => route.fullPath,
  () => {
    fetchUnreadCount();
    fetchAdminReviewTodo();
    fetchUserReviewTodo();
  }
);

watch(
  () => loginUser.value?.id,
  () => {
    fetchUnreadCount();
    fetchAdminReviewTodo();
    fetchUserReviewTodo();
  }
);

onMounted(() => {
  fetchUnreadCount();
  fetchAdminReviewTodo();
  fetchUserReviewTodo();
});
</script>

<style scoped>
.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--oj-bg);
}

.sidebar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 28px;
  min-height: 72px;
  padding: 0 clamp(20px, 5vw, 72px);
  background: var(--oj-panel);
  border-bottom: 1px solid var(--oj-border);
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.04);
}

.side-nav {
  display: flex;
  flex: 1;
  align-self: stretch;
  align-items: stretch;
  gap: 4px;
  min-width: 0;
}

.nav-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 0 0 auto;
  height: auto;
  padding: 0 14px;
  color: #334155;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 0;
  transition: all 0.18s ease;
}

.nav-count {
  flex: 0 0 auto;
}

.nav-item:hover {
  color: var(--oj-primary);
  background: #f8fbff;
}

.nav-item.active {
  color: var(--oj-primary);
  background: linear-gradient(
    to bottom,
    transparent calc(100% - 3px),
    var(--oj-primary) calc(100% - 3px)
  );
  font-weight: 600;
}

.nav-item.admin {
  color: #155e75;
}

.nav-divider {
  display: flex;
  align-items: center;
  margin: 0 2px 0 10px;
  color: var(--oj-muted);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.user-card {
  position: relative;
  display: grid;
  flex: 0 0 auto;
  grid-template-columns: 48px 36px;
  align-items: center;
  justify-items: center;
  gap: 6px;
  min-height: 0;
  padding: 5px 7px;
  background: #f8faff;
  border: 1px solid #e2e8f4;
  border-radius: 999px;
  box-shadow: none;
}

.user-card.authenticated {
  grid-template-columns: 36px 48px 36px;
}

.user-card-bg {
  display: none;
}

.notification-anchor {
  position: static;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
}

.notification-trigger {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  color: #2563eb;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #bfdbfe;
  border-radius: 50%;
  box-shadow: none;
  transition: all 0.18s ease;
}

.notification-trigger:hover {
  color: #1d4ed8;
  background: #eff6ff;
  transform: translateY(-1px);
}

.notification-trigger svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.notification-popover {
  width: 230px;
}

.notification-popover-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.notification-popover-head strong {
  color: var(--oj-text);
  font-size: 15px;
}

.notification-popover-head span {
  color: var(--oj-muted);
  font-size: 12px;
}

.notification-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.notification-summary div {
  padding: 8px;
  background: #f8fbff;
  border: 1px solid #dbeafe;
  border-radius: 6px;
}

.notification-summary span {
  display: block;
  color: var(--oj-muted);
  font-size: 12px;
}

.notification-summary strong {
  display: block;
  margin-top: 2px;
  color: var(--oj-primary);
  font-size: 18px;
}

.notification-popover-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.user-summary {
  display: contents;
}

.avatar-button {
  padding: 0;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 50%;
  outline: 3px solid #fff;
  box-shadow: 0 0 0 1px #dbe5f4, 0 5px 14px rgba(37, 99, 235, 0.12);
}

.avatar {
  font-size: 11px;
  letter-spacing: -0.04em;
  background: #dbeafe;
  border: 2px solid #fff;
  box-shadow: none;
}

.avatar :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dropdown-trigger {
  position: relative;
  top: auto;
  right: auto;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  color: #2563eb;
  cursor: pointer;
  background: #fff;
  border: 1px solid #bfdbfe;
  border-radius: 50%;
  box-shadow: none;
  transition: all 0.18s ease;
}

.dropdown-trigger:hover {
  color: #1d4ed8;
  background: #eff6ff;
  transform: translateY(-1px);
}

.dropdown-trigger svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.review-dot {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 8px;
  height: 8px;
  background: #f53f3f;
  border: 2px solid #fff;
  border-radius: 50%;
}

.space-option {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 72px;
}

.space-option-dot {
  width: 7px;
  height: 7px;
  background: #f53f3f;
  border-radius: 50%;
}

.user-copy {
  display: none;
}

.welcome {
  color: var(--oj-muted);
  font-size: 12px;
}

.name {
  width: 100%;
  margin: 3px 0 10px;
  overflow: hidden;
  color: var(--oj-text);
  font-size: 15px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-actions {
  display: none;
}

.logout-button {
  width: 100%;
  border-radius: 6px;
}

.logout-button:hover {
  background: #fff1f2;
}

.main-panel {
  flex: 1;
  min-width: 0;
  padding: 24px clamp(20px, 5vw, 72px) 40px;
}

.site-footer {
  display: flex;
  justify-content: center;
  padding: 0 20px 24px;
  color: var(--oj-muted);
  font-size: 13px;
}

.site-footer a {
  color: inherit;
  text-decoration: none;
}

.site-footer a:hover {
  color: var(--oj-primary);
}

@media (max-width: 900px) {
  .app-shell {
    min-width: 0;
  }

  .sidebar {
    position: relative;
    flex-wrap: wrap;
    gap: 0 16px;
    padding: 10px 20px 0;
  }

  .side-nav {
    order: 3;
    flex-basis: 100%;
    min-height: 48px;
    overflow-x: auto;
  }

  .nav-item {
    padding: 0 12px;
  }

  .main-panel {
    padding: 18px 20px 32px;
  }
}
</style>
